/**
 * /admin/feature-flags — Admin-only panel to flip held-back features live.
 *
 * Currently exposes:
 *   - `digest`         — Weekly Account Health Check + Monthly Account Review.
 *   - `titanium_live`  — Single switch that opens the entire Titanium tier
 *                       (Stripe checkout SKUs, comp dropdown, /pricing card).
 *
 * Flipping a card to ON makes the corresponding feature go live across
 * the app. Flip back OFF to pause cleanly without a redeploy.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ToggleRight, ShieldCheck, Sparkles, DoorOpen } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { isAdminOrAbove } from '../lib/tier';
import PageTitle from '../components/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FlagCard = ({
  icon: Icon, iconBg, iconColor, title, description, flagValue, onToggle,
  meta, saving, testIdPrefix,
}) => (
  <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid={`${testIdPrefix}-card`}>
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <Icon className={`w-4 h-4 ${iconColor}`} />
          <h2 className="font-bold text-[#020617]">{title}</h2>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${flagValue ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
            {flagValue ? 'LIVE' : 'HELD'}
          </span>
        </div>
        <p className="text-sm text-[#475569] leading-relaxed">{description}</p>
        {meta?.updated_at && (
          <p className="text-xs text-[#94A3B8] mt-2">
            Last changed {new Date(meta.updated_at).toLocaleString('en-GB')}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Label htmlFor={`${testIdPrefix}-switch`} className="text-sm">{flagValue ? 'On' : 'Off'}</Label>
        <Switch
          id={`${testIdPrefix}-switch`}
          checked={flagValue}
          onCheckedChange={onToggle}
          disabled={saving}
          data-testid={`${testIdPrefix}-toggle`}
        />
      </div>
    </div>
    {iconBg && null /* iconBg only used in older variants; kept for compat */}
  </div>
);

const AdminFeatureFlags = () => {
  const { user } = useAuth();

  // Digest (existing)
  const [digestFlag, setDigestFlag] = useState(false);
  const [digestMeta, setDigestMeta] = useState(null);

  // Titanium (new — single launch-day switch)
  const [titaniumFlag, setTitaniumFlag] = useState(false);
  const [titaniumMeta, setTitaniumMeta] = useState(null);

  // Public signups (hard gate before launch)
  const [signupsFlag, setSignupsFlag] = useState(false);
  const [signupsMeta, setSignupsMeta] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [digestRes, titaniumRes, signupsRes] = await Promise.all([
          axios.get(`${API}/admin/digest/feature-flag`, { withCredentials: true }),
          axios.get(`${API}/admin/feature-flags/titanium_live`, { withCredentials: true }),
          axios.get(`${API}/admin/feature-flags/public_signups_enabled`, { withCredentials: true }),
        ]);
        setDigestFlag(!!digestRes.data.enabled);
        setDigestMeta(digestRes.data);
        setTitaniumFlag(!!titaniumRes.data.enabled);
        setTitaniumMeta(titaniumRes.data);
        setSignupsFlag(!!signupsRes.data.enabled);
        setSignupsMeta(signupsRes.data);
      } catch (err) {
        toast.error('Could not load feature flags.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (!user) return <Navigate to="/login" replace />;
  if (!isAdminOrAbove(user)) return <Navigate to="/home" replace />;

  const toggleDigest = async (next) => {
    setSaving(true);
    setDigestFlag(next);
    try {
      const r = await axios.put(`${API}/admin/digest/feature-flag`, { enabled: next }, { withCredentials: true });
      setDigestMeta((m) => ({ ...(m || {}), ...r.data }));
      toast.success(next ? 'Account Health Check is now LIVE.' : 'Account Health Check paused.');
    } catch (err) {
      setDigestFlag(!next);
      toast.error('Could not update flag.');
    } finally {
      setSaving(false);
    }
  };

  const toggleTitanium = async (next) => {
    // Belt-and-braces confirmation on the OPEN side — flipping this is
    // launch day and triggers real Stripe charges, so a tiny check
    // beats an accidental click.
    if (next && !window.confirm(
      "Open the Titanium tier?\n\n"
      + "This will:\n"
      + "  • Make Titanium SKUs buyable via Stripe checkout (£10/mo, £100/yr)\n"
      + "  • Allow staff to comp users to Titanium via /admin/tiers\n"
      + "  • Swap the /pricing Titanium waitlist card for a real checkout button\n\n"
      + "Flip OFF anytime to pause."
    )) return;
    setSaving(true);
    setTitaniumFlag(next);
    try {
      const r = await axios.put(`${API}/admin/feature-flags/titanium_live`, { enabled: next }, { withCredentials: true });
      setTitaniumMeta((m) => ({ ...(m || {}), ...r.data }));
      toast.success(next ? 'Titanium is now LIVE.' : 'Titanium paused (waitlist mode).');
    } catch (err) {
      setTitaniumFlag(!next);
      toast.error('Could not update flag.');
    } finally {
      setSaving(false);
    }
  };

  const toggleSignups = async (next) => {
    // Opening signups exposes /register + Google first-sign-in to the
    // public. Confirm intent so we don't open the doors by accident.
    if (next && !window.confirm(
      "Open public signups?\n\n"
      + "This will:\n"
      + "  • Allow anyone to create a Forge Ledger account via /register\n"
      + "  • Allow first-time Google sign-ins to provision an account\n"
      + "  • Swap the landing-page 'Join the launch list' CTA for 'Create free account'\n\n"
      + "Flip OFF anytime to re-close the doors (existing users keep their accounts)."
    )) return;
    setSaving(true);
    setSignupsFlag(next);
    try {
      const r = await axios.put(`${API}/admin/feature-flags/public_signups_enabled`, { enabled: next }, { withCredentials: true });
      setSignupsMeta((m) => ({ ...(m || {}), ...r.data }));
      toast.success(next ? 'Public signups are now OPEN.' : 'Public signups CLOSED — waitlist mode.');
    } catch (err) {
      setSignupsFlag(!next);
      toast.error('Could not update flag.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 sm:p-8" data-testid="admin-feature-flags-page">
      <Link to="/admin/tiers" className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0052FF] mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to admin
      </Link>

      <PageTitle
        title="Feature flags"
        subtitle="Flip held-back features live without a redeploy."
      />

      {loading ? (
        <div className="text-center text-[#64748B] p-6">Loading flags…</div>
      ) : (
        <div className="space-y-4">
          <FlagCard
            icon={DoorOpen}
            iconColor="text-[#0052FF]"
            title="Public signups (hard gate)"
            description={
              'PRE-LAUNCH HARD GATE. When OFF: the landing page swaps "Create free account" '
              + 'for "Join the launch list", /register redirects back to /, the backend returns '
              + '403 on POST /auth/register, and first-time Google sign-ins are blocked. Existing '
              + 'users (including admin/owner) keep signing in normally. Flip ON to open the doors '
              + 'on launch day.'
            }
            flagValue={signupsFlag}
            onToggle={toggleSignups}
            meta={signupsMeta}
            saving={saving}
            testIdPrefix="feature-flag-signups"
          />
          <FlagCard
            icon={ShieldCheck}
            iconColor="text-[#0052FF]"
            title="Account Health Check"
            description={
              'Weekly Account Health Check (Steel) + Weekly Account Health Check+ & Monthly Account Review (Damascus). '
              + 'When OFF: the user-facing settings page shows a "coming soon" placeholder, the scheduler skips every user, '
              + 'and preview/PUT endpoints return 503. Flipping ON makes everything go live immediately.'
            }
            flagValue={digestFlag}
            onToggle={toggleDigest}
            meta={digestMeta}
            saving={saving}
            testIdPrefix="feature-flag-digest"
          />
          <FlagCard
            icon={Sparkles}
            iconColor="text-[#7C3AED]"
            title="Titanium tier (launch switch)"
            description={
              'SINGLE LAUNCH SWITCH for the entire Titanium tier. When ON: Stripe checkout for titanium_monthly / '
              + 'titanium_annual SKUs is unlocked, `POST /auth/me/tier` accepts pro_premium so staff can comp users '
              + 'via /admin/tiers, and the /pricing Titanium card swaps the waitlist form for a real "Forge with Titanium" '
              + 'checkout button. When OFF: every Titanium surface falls back to the waitlist + "coming soon" state. '
              + 'Open Banking + The Smith do NOT depend on this flag (they have their own gates).'
            }
            flagValue={titaniumFlag}
            onToggle={toggleTitanium}
            meta={titaniumMeta}
            saving={saving}
            testIdPrefix="feature-flag-titanium"
          />
        </div>
      )}
    </div>
  );
};

export default AdminFeatureFlags;
